/**
 * ViewAsBanner — the persistent "Viewing as X — read-only · Exit" strip shown
 * while a platform admin is impersonating another user (spec §7/§8).
 *
 * Detection is via Clerk's `actor` claim (set only on an impersonation session),
 * so the banner renders null for every normal session with zero visual impact.
 * The `__force` prop is for the offline harness, which has no real Clerk session.
 */
import React from 'react';
import { ShieldAlert, LogOut, Loader2 } from 'lucide-react';
import { useAuth, useUser } from '../../contexts/TestAuthContext';
import { useViewAs } from '../../hooks/useViewAs';

interface ViewAsBannerProps {
  /** Harness-only: force the impersonating state with a given target label. */
  __force?: { email?: string } | boolean;
}

export default function ViewAsBanner({ __force }: ViewAsBannerProps) {
  const auth = useAuth() as any;
  const { user } = useUser() as any;
  const { stop, busy } = useViewAs();

  const forced = !!__force;
  const impersonating = forced || !!auth?.actor;
  if (!impersonating) return null;

  const forcedEmail = typeof __force === 'object' && __force ? __force.email : undefined;
  const email =
    forcedEmail ||
    user?.primaryEmailAddress?.emailAddress ||
    user?.emailAddresses?.[0]?.emailAddress ||
    'this user';

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '8px 16px',
        background: 'var(--color-brand)',
        color: 'var(--color-on-brand)',
        borderBottom: '1px solid rgba(0,0,0,0.25)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <ShieldAlert style={{ width: 18, height: 18, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Viewing as <strong>{email}</strong>
        </span>
        <span
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-mono, monospace)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '1px 6px',
            borderRadius: 3,
            border: '1px solid var(--color-on-brand)',
            whiteSpace: 'nowrap',
          }}
        >
          Read-only
        </span>
      </div>
      <button
        type="button"
        onClick={() => stop()}
        disabled={busy}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          fontFamily: 'var(--font-mono, monospace)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          padding: '4px 10px',
          borderRadius: 4,
          background: 'var(--color-on-brand)',
          color: 'var(--color-brand)',
          border: 'none',
          cursor: busy ? 'wait' : 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {busy ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <LogOut style={{ width: 13, height: 13 }} />}
        Exit view-as
      </button>
    </div>
  );
}
